import clsx from "clsx";
import { TonePill, ToneMarker, useApi, useT, type Tone } from "@iga/ui";
import type { TaskStatus } from "../lib/types";
import { useFmt } from "../lib/hooks";

const TERMINAL = new Set(["confirmed", "failed", "cancelled"]);
const STAGE_TONE: Record<TaskStatus["stage"], Tone> = { queued: "neutral", sent: "info", confirmed: "ok", retrying: "warn", failed: "bad", cancelled: "neutral" };

/** Poll the live state of service-desk actions every second until all are finished (PLAN-PHASE-2.md 2.9). */
export function useTaskStatus(ids: string[]) {
  const q = useApi<{ items: TaskStatus[] }>(["sd-tasks", ids.join(",")], "/api/portal/servicedesk/tasks", { ids: ids.join(",") }, {
    enabled: ids.length > 0,
    refetchInterval: (query) => ((query.state.data as { items: TaskStatus[] } | undefined)?.items ?? []).every((t) => TERMINAL.has(t.stage)) && query.state.data ? false : 1000,
  });
  return q;
}

/**
 * queued → sent to <system> → confirmed by <system>, with the time of each step; or retrying / failed with
 * the last error. Never claims "done" before the target system confirmed it.
 */
export function ActionProgress({ task, compact }: { task: TaskStatus; compact?: boolean }) {
  const t = useT();
  const fmt = useFmt();
  const steps: { key: string; label: string; at: string | null; state: "done" | "current" | "todo" | "bad" }[] = [
    { key: "queued", label: t("p.sd.stage.queued"), at: task.createdAt, state: "done" },
    { key: "sent", label: t("p.sd.stage.sent", { system: task.systemName }), at: task.sentAt, state: task.sentAt ? (task.stage === "sent" ? "current" : "done") : task.stage === "queued" ? "current" : "todo" },
    { key: "confirmed", label: t("p.sd.stage.confirmed", { system: task.systemName }), at: task.confirmedAt, state: task.stage === "confirmed" ? "done" : task.stage === "failed" ? "bad" : "todo" },
  ];
  if (compact) {
    return (
      <TonePill tone={STAGE_TONE[task.stage]} compact>
        {t(`p.sd.stageShort.${task.stage}`, { system: task.systemName, time: fmt.time(task.confirmedAt ?? task.finishedAt ?? task.sentAt ?? task.createdAt) })}
      </TonePill>
    );
  }
  return (
    <div className="flex flex-col gap-2" aria-live="polite">
      <ol className="flex flex-col gap-1.5">
        {steps.map((s) => (
          <li key={s.key} className={clsx("flex items-center gap-2", s.state === "todo" && "text-fg-3")}>
            <span className="inline-flex w-4 justify-center">
              {s.state === "done" ? <span className="text-ok"><ToneMarker tone="ok" /></span>
                : s.state === "bad" ? <span className="text-bad"><ToneMarker tone="bad" /></span>
                : s.state === "current" ? <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-brand" aria-hidden="true" />
                : <span className="h-2 w-2 rounded-full border border-line-strong" aria-hidden="true" />}
            </span>
            <span className={clsx(s.state === "current" && "font-semibold")}>{s.label}</span>
            {s.at && <span className="ml-auto text-small text-fg-3 tabular-nums">{fmt.time(s.at)}</span>}
          </li>
        ))}
      </ol>
      {task.stage === "queued" && task.lastError && <p className="text-small text-fg-2">{task.lastError}{task.nextAttemptAt ? ` · ${t("p.sd.nextTry", { next: fmt.time(task.nextAttemptAt) })}` : ""}</p>}
      {task.stage === "retrying" && <p className="text-small text-warn">{t("p.sd.retrying", { attempts: task.attempts, next: task.nextAttemptAt ? fmt.time(task.nextAttemptAt) : "–", error: task.lastError ?? "" })}</p>}
      {task.stage === "failed" && <p className="text-small text-bad">{t("p.sd.failedAction", { error: task.lastError ?? "" })}</p>}
    </div>
  );
}
